/**
 * Monitor rules → one action. Pure: no storage, no bindings, no model call.
 *
 * A monitor produces variables; this module decides what they mean. The rules and
 * the tool names are admin config, the variables are the model's only contribution,
 * and the two meet here — so the model never names a tool and never supplies an
 * argument the config did not ask for.
 *
 * Comparison stays in `./flag-conditions`: this module builds the variable lookup
 * and evaluates single conditions through that module's own exports, so `>=`
 * means one thing and a variable resolves to one column. A rule's `when` is AND
 * while `flag_conditions` is OR — a difference in quantification, not in
 * comparison.
 *
 * `MonitorAction` and `MonitorRule` are inferred from the schemas in the agent
 * contract, so the shape this interprets is the shape that contract admits. The
 * import is type-only and leaves no runtime edge to the contract.
 */

import {
  buildFlagConditionLookup,
  evaluateFlagCondition,
  type FlagCondition,
  type FlagConditionVariable,
} from './flag-conditions';
import type { MonitorAction, MonitorRule } from './contracts/setup/agents';

/** The rules slice of a monitor's config — all this interpreter reads. */
export interface MonitorRulesConfig {
  rules?: MonitorRule[] | null;
  fallback?: MonitorAction | null;
}

/**
 * An action with its arguments resolved to values.
 *
 * `MonitorAction` describes where each argument COMES FROM; this describes what
 * each one IS. They are separate types because a caller handing
 * `{ from_variable: 'urgency' }` to a tool would pass the mapping itself as the
 * argument. Only the `call_tool` arm differs, so the others are taken from
 * `MonitorAction` rather than restated.
 *
 * Deriving them means this type WIDENS with `MonitorAction` rather than rejecting
 * a new arm, so it is not the thing that makes a new action kind be handled — the
 * exhaustive switch in `resolveAction` is. Do not delete that switch's `never`
 * arm on the grounds that the types already line up; lining up is what the arm
 * exists to stop.
 */
export type ResolvedMonitorAction =
  | Exclude<MonitorAction, { kind: 'call_tool' }>
  | { kind: 'call_tool'; tool_name: string; args: Record<string, string | number | boolean> };

/**
 * Why a selected action was not resolved.
 *
 * `tool_not_assigned` — the rule names a tool the monitor does not carry.
 * `argument_not_mapped` — an argument's mapping is neither a constant nor a
 * variable name, so there is nothing to pass. `{ reason: 'sentiment' }` written
 * where `{ reason: { from_variable: 'sentiment' } }` was meant is that shape.
 * `variable_not_resolved` — an argument maps from a variable the monitor did not
 * produce this run, so the tool would be called with the argument missing.
 *
 * A refusal always names something. Config too malformed to name — a rule that is
 * not a record, an action kind no arm knows — resolves to no action and no
 * refusal instead.
 */
export type MonitorDecisionRefusalReason =
  | 'tool_not_assigned'
  | 'argument_not_mapped'
  | 'variable_not_resolved';

export interface MonitorDecisionRefusal {
  reason: MonitorDecisionRefusalReason;
  /**
   * The tool name for `tool_not_assigned`, the argument name for
   * `argument_not_mapped`, the variable name for `variable_not_resolved`.
   */
  subject: string;
}

export interface MonitorDecisionResult {
  /** What to carry out. `{ kind: 'none' }` whenever nothing matched or the selected action was refused. */
  action: ResolvedMonitorAction;
  /** Index in `rules` of the first rule that matched, or null when `fallback` supplied the action. */
  matched_rule_index: number | null;
  /** Set when the selected action was refused, so the refusal can be recorded rather than inferred. */
  refusal: MonitorDecisionRefusal | null;
}

/** The variables a decision is taken over. */
export interface MonitorDecisionVariables {
  /** The monitor's own variables, in the shape the flag-condition evaluator takes. */
  custom: readonly FlagConditionVariable[];
  /**
   * System metrics, which a condition resolves ahead of `custom`. Conditions may
   * read them; arguments may not — an argument maps from what the monitor
   * decided, not from the conversation's own counters.
   */
  system_metrics?: Record<string, unknown>;
}

/**
 * Resolve a monitor's variables to one action: the first rule whose conditions all
 * hold, else `fallback`, else nothing.
 *
 * A refused action does NOT fall through to the next rule. First match wins, and a
 * rule that matched but could not be carried out is a configuration error to
 * report — resuming the scan would run the admin's second choice under conditions
 * they wrote for their first.
 */
export function interpretMonitorDecision(
  variables: MonitorDecisionVariables,
  config: MonitorRulesConfig | null | undefined,
  assignedTools: readonly string[],
): MonitorDecisionResult {
  // One lookup for the whole decision: the conditions compare against it and the
  // arguments resolve from it, so a repeated variable name cannot mean one row to
  // a rule and another to the tool that rule selects.
  const lookup = buildFlagConditionLookup(variables.custom);
  const systemMetrics = variables.system_metrics ?? {};
  const rules = Array.isArray(config?.rules) ? config.rules : [];

  for (let index = 0; index < rules.length; index++) {
    const rule = rules[index];
    if (!ruleMatches(rule, systemMetrics, lookup)) continue;
    return { ...resolveAction(rule.action, lookup, assignedTools), matched_rule_index: index };
  }

  const fallback = config?.fallback ?? { kind: 'none' as const };
  return { ...resolveAction(fallback, lookup, assignedTools), matched_rule_index: null };
}

/**
 * AND across a rule's conditions, one call to the shared evaluator per condition.
 *
 * A rule that is not a record, or whose `when` is not a non-empty array of
 * records, does not match. `monitorRuleSchema` admits none of those, but see
 * `isRecord` for why what reaches this function is not what that schema admits;
 * and `Array.every` on an empty array would make such a rule fire on every run.
 */
function ruleMatches(
  rule: MonitorRule,
  systemMetrics: Record<string, unknown>,
  lookup: ReadonlyMap<string, string | number | null>,
): boolean {
  if (!isRecord(rule)) return false;
  const conditions: unknown = rule.when;
  if (!Array.isArray(conditions) || conditions.length === 0) return false;
  return conditions.every(
    (condition) =>
      isRecord(condition) && evaluateFlagCondition(condition as unknown as FlagCondition, systemMetrics, lookup),
  );
}

/**
 * Carry out the selected action's resolution, per kind.
 *
 * The switch is exhaustive over `MonitorAction` and the `never` arm is the whole
 * point of writing it as a switch: an action kind added to `monitorActionSchema`
 * fails typecheck HERE, where someone has to say whether it takes arguments and
 * what refuses it. A `kind !== 'call_tool'` early return reads the same and
 * accepts a new kind silently, passing it to the caller as resolved.
 *
 * Every payload an arm returns is checked before it is returned, because the
 * result is what a caller hands onward: a `rewrite` carries a non-empty string
 * note or it is no action at all. An action this function cannot make sense of
 * yields `{ kind: 'none' }` with no refusal — there is nothing to name.
 */
function resolveAction(
  action: MonitorAction,
  lookup: ReadonlyMap<string, string | number | null>,
  assignedTools: readonly string[],
): Pick<MonitorDecisionResult, 'action' | 'refusal'> {
  if (!isRecord(action)) return NO_ACTION;

  switch (action.kind) {
    case 'none':
    case 'hold':
      return { action: { kind: action.kind }, refusal: null };
    case 'rewrite':
      return isNonEmptyString(action.note) ? { action: { kind: 'rewrite', note: action.note }, refusal: null } : NO_ACTION;
    // The note is the CONFIG's string, handed on untouched: a steer says only what the hub
    // wrote. Blank is no action at all, as for a rewrite.
    case 'steer':
      return isNonEmptyString(action.note) && action.note.trim() !== ''
        ? { action: { kind: 'steer', note: action.note }, refusal: null }
        : NO_ACTION;
    case 'call_tool':
      return resolveToolCall(action, lookup, assignedTools);
    default: {
      const unreachable: never = action;
      void unreachable;
      return NO_ACTION;
    }
  }
}

/** Check the tool against the monitor's own list, then resolve each argument. */
function resolveToolCall(
  action: Extract<MonitorAction, { kind: 'call_tool' }>,
  lookup: ReadonlyMap<string, string | number | null>,
  assignedTools: readonly string[],
): Pick<MonitorDecisionResult, 'action' | 'refusal'> {
  // A name that is not a string names no tool, and there is no string to record
  // against it, so it refuses the way an unknown action kind does.
  if (!isNonEmptyString(action.tool_name)) return NO_ACTION;
  if (!assignedTools.includes(action.tool_name)) {
    return { action: { kind: 'none' }, refusal: { reason: 'tool_not_assigned', subject: action.tool_name } };
  }

  // `args` is either absent or a mapping. Anything else is not a mapping this
  // function can read entry by entry, and calling the tool with none of the
  // arguments the admin wrote would be a different call than the one configured.
  const rawArgs: unknown = action.args;
  if (rawArgs !== undefined && !isRecord(rawArgs)) return NO_ACTION;

  const args: Record<string, string | number | boolean> = {};
  for (const [name, source] of Object.entries(rawArgs ?? {})) {
    if (!isRecord(source)) {
      return { action: { kind: 'none' }, refusal: { reason: 'argument_not_mapped', subject: name } };
    }
    if ('const' in source) {
      if (!isArgumentValue(source.const)) {
        return { action: { kind: 'none' }, refusal: { reason: 'argument_not_mapped', subject: name } };
      }
      args[name] = source.const;
      continue;
    }
    if (!isNonEmptyString(source.from_variable)) {
      return { action: { kind: 'none' }, refusal: { reason: 'argument_not_mapped', subject: name } };
    }
    // The lookup carries what the variable's own type stored, and the tool's
    // schema validates it. Widening a stored `"true"` into a boolean here would
    // decide for a tool that has not been consulted yet.
    const value = lookup.get(source.from_variable) ?? null;
    if (value === null) {
      return {
        action: { kind: 'none' },
        refusal: { reason: 'variable_not_resolved', subject: source.from_variable },
      };
    }
    args[name] = value;
  }

  return { action: { kind: 'call_tool', tool_name: action.tool_name, args }, refusal: null };
}

const NO_ACTION: Pick<MonitorDecisionResult, 'action' | 'refusal'> = { action: { kind: 'none' }, refusal: null };

/**
 * The run-time shape check every read of a monitor's config goes through.
 *
 * The parameters above are typed from `monitorActionSchema` and `monitorRuleSchema`,
 * and at run time nothing here has been through either: `monitor_config` is a
 * `.passthrough()` column, so `rules` arrives as whatever JSON a REST body or a
 * `wayai push` stored. A bare property read on it is a `TypeError` on the monitor
 * path for an admin's malformed YAML, which is why each read is guarded rather
 * than each known-bad shape enumerated.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value !== '';
}

/** What a tool argument may be — the three primitives `const` admits. */
function isArgumentValue(value: unknown): value is string | number | boolean {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
}
